/**
 * AiVerifyCallButton.jsx — places a real outbound verification call and
 * shows live status. Uses the real placeCall/getCallStatus/endCall/
 * getRecording Redux actions instead of a direct fetch() to a local
 * backend.
 *
 * IMPORTANT KNOWN LIMITATION: the backend's callResult state
 * (state.aiVerificationCall.callResult) is a single shared slice, not
 * keyed per job — if two of these buttons are actively polling at the
 * exact same time (two different doctors' calls in progress
 * simultaneously), their status updates can briefly interleave. This
 * component guards against showing another call's data by checking
 * callResult.jobId against its own tracked job before trusting it, but
 * this is a real backend design constraint, not something fixable
 * purely from this component.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Grow from "@mui/material/Grow";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import CallEndRoundedIcon from "@mui/icons-material/CallEndRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import {
  placeCall as placeCallAction,
  getCallStatus,
  endCall as endCallAction,
  getRecording,
  getBillingLogs,
  resetCallResult,
} from "../redux/ducks/aiVerificationCall";
import classes from "./AiVerifyCallButton.module.scss";

// #0f9d8f — the app's teal token — for the idle/default button. Active
// states use real semantic tokens (amber = in progress, green/red =
// the actual verified outcome) instead of one flat color throughout.
const STATE_COLORS = {
  idle: "#0f9d8f",
  pending: "#c9760a",
  success: "#1f8a4c",
  error: "#c23b3b",
};

function normalizeToE164(rawNumber) {
  if (!rawNumber) return rawNumber;
  const trimmed = String(rawNumber).trim();
  if (trimmed.startsWith("+")) return trimmed;
  const digitsOnly = trimmed.replace(/\D/g, "");
  if (digitsOnly.length === 10) return `+1${digitsOnly}`;
  if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) return `+${digitsOnly}`;
  return trimmed;
}

function getButtonColor(status, result) {
  if (status === "dialing" || status === "in_progress") return STATE_COLORS.pending;
  if (status === "completed") {
    if (result?.acceptsWorkersComp === "yes") return STATE_COLORS.success;
    if (result?.acceptsWorkersComp === "no") return STATE_COLORS.error;
    return STATE_COLORS.pending;
  }
  if (status === "pending" || status === "failed") return STATE_COLORS.pending;
  return STATE_COLORS.idle;
}

function formatElapsed(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

const GrowTransition = (props) => <Grow {...props} timeout={220} />;

export default function AiVerifyCallButton({
  doctorName = "",
  doctorSpecialty = "",
  doctorFaxNumber = "",
  doctorAddress = "",
  initialPhoneNumber = "",
  organizationId = "",
  organizationName = "",
  userId = "",
  userName = "",
}) {
  const dispatch = useDispatch();
  const { callResult, recordingUrlsByJobId } = useSelector((state) => state.aiVerificationCall);

  const [open, setOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const jobIdRef = useRef(null);
  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const autoCloseTimerRef = useRef(null);

  // Only trust callResult when it actually corresponds to OUR job —
  // guards against the shared-state limitation described above.
  const result = callResult && callResult.jobId === jobIdRef.current ? callResult : null;

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const stopElapsedTimer = useCallback(() => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!result) return;
    setStatus(result.status || status);
    if (["completed", "pending", "failed"].includes(result.status)) {
      stopPolling();
      stopElapsedTimer();
      if (result.recordingUrl && !recordingUrlsByJobId[result.jobId]) {
        dispatch(getRecording(result.jobId));
      }
      // Refresh the shared billing logs state now, not just on next
      // LogsTable mount — without this, a call placed from one page
      // (e.g. a doctor's search-result card) wouldn't show up in the
      // Billing view until something forced LogsTable to fully remount
      // (like a full logout/login), even though the backend already
      // saved it correctly.
      dispatch(getBillingLogs());
      autoCloseTimerRef.current = setTimeout(() => setOpen(false), 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  useEffect(() => {
    return () => {
      stopPolling();
      stopElapsedTimer();
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    };
  }, [stopPolling, stopElapsedTimer]);

  const handleOpen = () => {
    setPhoneNumber(initialPhoneNumber);
    setOpen(true);
  };

  const handleClose = () => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    setOpen(false);
  };

  const handlePlaceCall = () => {
    setError(null);
    if (!phoneNumber || !phoneNumber.trim()) {
      setError("Enter a phone number first.");
      return;
    }
    setStatus("dialing");
    const startedAt = new Date();
    setCallStartedAt(startedAt);
    setElapsedSeconds(0);
    stopElapsedTimer();
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt.getTime()) / 1000));
    }, 1000);
    dispatch(resetCallResult());
    dispatch(
      placeCallAction({
        phoneNumber: normalizeToE164(phoneNumber.trim()),
        doctorName: doctorName ? doctorName.trim() : undefined,
        doctorSpecialty: doctorSpecialty ? doctorSpecialty.trim() : undefined,
        doctorFaxNumber: doctorFaxNumber ? doctorFaxNumber.trim() : undefined,
        doctorAddress: doctorAddress ? doctorAddress.trim() : undefined,
        organizationId: organizationId || undefined,
        organizationName: organizationName || undefined,
        userId: userId || undefined,
        userName: userName || undefined,
      })
    );
  };

  // Start polling once we see a jobId show up in callResult after placing.
  useEffect(() => {
    if (status === "dialing" && callResult?.jobId && !jobIdRef.current) {
      jobIdRef.current = callResult.jobId;
      stopPolling();
      pollTimerRef.current = setInterval(() => {
        dispatch(getCallStatus(jobIdRef.current));
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, callResult]);

  const handleEndCall = () => {
    if (!jobIdRef.current) return;
    dispatch(endCallAction(jobIdRef.current));
  };

  const isActive = status === "dialing" || status === "in_progress";
  const isQueued = status === "dialing" && !jobIdRef.current;
  const recordingBlobUrl = result?.jobId ? recordingUrlsByJobId[result.jobId] : null;

  // Status card tone/copy driven by the REAL status — not a hardcoded
  // "always amber" like a static mockup would show.
  let statusTone = "amber";
  let statusTitle = "Calling…";
  if (isActive) {
    statusTone = "amber";
    statusTitle = status === "in_progress" ? "On the call — asking about Workers' Comp…" : "Calling…";
  } else if (status === "completed" && result) {
    if (result.acceptsWorkersComp === "yes") {
      statusTone = "green";
      statusTitle = "Verified — accepts Workers' Comp";
    } else if (result.acceptsWorkersComp === "no") {
      statusTone = "red";
      statusTitle = "Verified — does not accept Workers' Comp";
    } else {
      statusTone = "gray";
      statusTitle = "Call completed — result pending";
    }
  } else if (status === "failed") {
    statusTone = "red";
    statusTitle = "Call failed";
  } else if (status === "pending") {
    statusTone = "gray";
    statusTitle = "Pending";
  }

  const showStatusCard = isActive || (status === "completed" && result) || status === "failed";

  // A brief pulse the moment a definitive result actually arrives —
  // distinguishing "a result just landed" from the ordinary smooth
  // color transition every status change already gets. Tracks the
  // previous status via a ref so this only fires once, right when
  // completed first becomes true, not on every re-render while
  // already completed.
  const prevStatusRef = useRef(status);
  const [justResolved, setJustResolved] = useState(false);
  useEffect(() => {
    if (status === "completed" && prevStatusRef.current !== "completed") {
      const reducedMotion = !window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
      if (!reducedMotion) {
        setJustResolved(true);
        const t = setTimeout(() => setJustResolved(false), 500);
        return () => clearTimeout(t);
      }
    }
    prevStatusRef.current = status;
  }, [status]);

  return (
    <>
      <button
        onClick={handleOpen}
        className={`${classes["trigger-btn"]} ${justResolved ? classes["result-pulse"] : ""}`}
        style={{ background: getButtonColor(status, result) }}
      >
        {isActive ? (
          <CircularProgress size={14} sx={{ color: "white" }} />
        ) : (
          <PhoneRoundedIcon sx={{ fontSize: 15 }} />
        )}
        AI Verify Call
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={GrowTransition}
        PaperProps={{ className: classes["dialog-paper"] }}
        BackdropProps={{ className: classes["blur-backdrop"] }}
      >
        <div className={classes["modal-head"]}>
          <p className={classes["modal-title"]}>Verify workers' comp acceptance</p>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            size="small"
            className={classes["close-btn"]}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </div>

        <div className={classes["modal-body"]}>
          <div>
            <span className={classes["field-label"]}>Doctor</span>
            <div className={classes["field-static"]}>{doctorName || "—"}</div>
          </div>

          <div>
            <span className={classes["field-label"]}>Phone number</span>
            <input
              className={classes["field-input"]}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={isActive}
              placeholder="+1 555 123 4567"
            />
            {error && <p className={classes["field-error"]}>{error}</p>}
          </div>

          {showStatusCard && (
            <div className={`${classes["status-card"]} ${classes[`tone-${statusTone}`]}`}>
              <span
                className={`${classes["status-dot"]} ${isActive ? classes["pulsing"] : ""}`}
              ></span>
              <div className={classes["status-text"]}>
                <p className={classes["status-title"]}>{statusTitle}</p>
                {callStartedAt && (
                  <p className={classes["status-meta"]}>
                    Started{" "}
                    {callStartedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}{" "}
                    · {callStartedAt.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
              {isActive && <span className={classes["status-timer"]}>{formatElapsed(elapsedSeconds)}</span>}
            </div>
          )}

          {isQueued && (
            <div className={classes["queue-line"]}>
              <ScheduleRoundedIcon sx={{ fontSize: 14 }} />
              Queued — calling office…
            </div>
          )}

          {!isActive && status === "completed" && result?.fullTranscript && (
            <div className={classes["transcript-box"]}>{result.fullTranscript}</div>
          )}

          {!isActive && recordingBlobUrl && (
            <audio controls preload="none" src={recordingBlobUrl} style={{ width: "100%" }} />
          )}
        </div>

        <div className={classes["actions"]}>
          {isActive ? (
            <>
              <button className={`${classes["btn"]} ${classes["btn-ghost"]}`} disabled>
                Calling…
              </button>
              <button
                className={`${classes["btn"]} ${classes["btn-danger"]}`}
                onClick={handleEndCall}
              >
                <CallEndRoundedIcon sx={{ fontSize: 15 }} />
                End call
              </button>
            </>
          ) : (
            <button
              className={`${classes["btn"]} ${classes["btn-primary"]}`}
              onClick={handlePlaceCall}
            >
              <PhoneRoundedIcon sx={{ fontSize: 15 }} />
              {status === "completed" ? "Call again" : "Call"}
            </button>
          )}
        </div>
      </Dialog>
    </>
  );
}